import type {
  ColDef,
  GetQuickFilterTextParams,
  ITooltipParams,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { IAuthorization } from '~/types/authorization'
import type { IBadgeCellRendererParams } from '~/types/badge'

import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { authorizationsComparator, textComparator } from '~/utils/comparators'
import { badgeColDef, lastUpdateColDef, textColDef } from '~/utils/table'

const AuthorizationCellRenderer = ({ data }: ICellRendererParams): ReactElement => {
  const { description, key } = data as IAuthorization

  return (
    <div className="authorization-cell">
      <div className="authorization-cell__name text">{key}</div>
      <div className="authorization-cell__description text">{description}</div>
    </div>
  )
}

const columns: ColDef[] = [
  {
    cellRenderer: AuthorizationCellRenderer,
    comparator: authorizationsComparator,
    field: 'identity',
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['clear'],
      debounceMs: 1000,
      textCustomComparator: textComparator,
      trimInput: true,
      valueGetter: ({ data }: ValueGetterParams<IAuthorization>): string | undefined => {
        if (!data) return undefined

        return data.key
      },
    },
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IAuthorization>): string => {
      if (!data) return ''

      return data.key
    },
    headerName: 'Authorization',
    menuTabs: ['filterMenuTab'],
    minWidth: 250,
    tooltipComponent: 'customTooltip',
    tooltipValueGetter: ({ data }: ITooltipParams) => data,
    width: 280,
  },
  textColDef(TextCellRenderer, {
    field: 'service',
    headerName: 'Service',
    resizable: false,
  }),

  textColDef(TextCellRenderer, {
    field: 'resource',
    headerName: 'Resource',
    resizable: false,
  }),

  textColDef(TextCellRenderer, {
    field: 'action',
    headerName: 'Action',
    resizable: false,
  }),

  badgeColDef(BadgeCellRenderer, {
    field: 'roles',
    filterValueGetter: ({ data }: ValueGetterParams<IAuthorization>): string[] | undefined =>
      data?.roles?.map(({ name }) => name),
    headerName: 'Owner roles',
    valueGetter: ({ data }: ValueGetterParams<IAuthorization>): IBadgeCellRendererParams | undefined => {
      if (!data) return undefined

      const { roles } = data

      return {
        data:
          roles?.map(({ name, role_id }) => ({
            id: role_id,
            name,
          })) || [],
        type: 'role',
      }
    },
    width: 300,
  }),
  lastUpdateColDef({
    field: 'last_update',
  }),
]

export default columns
